import getCookie from './getCookie';
import setCookie from './setCookie';
import { UserLocation } from './contracts/UserLocation';

const defaultLocation = {
  city: 'Atlanta',
  state: 'GA',
  zip: '30308'
};
const CITY_COOKIE = 'CVCurrentCity';
const STATE_COOKIE = 'CVCurrentState';
const ZIP_COOKIE = 'CVCurrentZip';

/**
 * Attempts to pull a value (by name) from an object (which can be a server-side
 * cookie store, JSON, or anything else like that), but if it _doesn't_ exist there
 * will fall through to pull it from browser-side cookies.
 */
export function fromCookies(cookieName: string, serverCookies: {}) {
  return serverCookies ? serverCookies[cookieName] : getCookie(cookieName);
}

/**
 * Commits a user location pieces to separate (browser-side) cookies.
 * The names of the browser-side cookies being used will be:
 *  - `CVCurrentCity`
 *  - `CVCurrentState`
 *  - `CVCurrentZip`
 *
 */
export function saveLocationToCookies(
  city: string,
  state: string,
  zip: string | number
) {
  setCookie(CITY_COOKIE, city);
  setCookie(STATE_COOKIE, state);
  setCookie(ZIP_COOKIE, zip);
}

/**
 * Checks to see if a `city`, `state` and `zip` field on a given location Object
 * matches the individual cookies (`CVCurrentCity`, `CVCurrentState`, and `CVCurrentZip`)
 * being stored for the current user.
 */
export function doesUserLocationMatchCookies({
  city,
  state,
  zip
}: UserLocation = {}) {
  return (
    city === getCookie(CITY_COOKIE) &&
    state === getCookie(STATE_COOKIE) &&
    `${zip}` === `${getCookie(ZIP_COOKIE)}`
  );
}

/**
 * Creates a user location Object (which simply contains `city`, `state` and
 * `zip` props) that favors values you pass in, but falls back onto indivial
 * city/state/zip browser-side cookies (`CVCurrentCity`, `CVCurrentState`, and `CVCurrentZip`)
 * if available and finally then to hard-coded values based in Atlanta, GA
 */
export function makeUserLocation(
  existingLocation: UserLocation,
  serverCookies: {}
): UserLocation {
  return {
    city:
      (existingLocation && existingLocation.city) ||
      fromCookies(CITY_COOKIE, serverCookies) ||
      defaultLocation.city,
    state:
      (existingLocation && existingLocation.state) ||
      fromCookies(STATE_COOKIE, serverCookies) ||
      defaultLocation.state,
    zip:
      (existingLocation && existingLocation.zip) ||
      fromCookies(ZIP_COOKIE, serverCookies) ||
      defaultLocation.zip
  };
}
